import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert, Box, Button, ButtonBase, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, IconButton, InputAdornment, Radio, RadioGroup, Skeleton, Stack, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { teamApi } from '../services/api';
import { useShell } from '../components/Layout';
import PageHeader from '../components/common/PageHeader';
import MarketplaceIcon from '../components/common/MarketplaceIcon';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { ErrorState } from '../components/common/States';
import { apiError, formatDate, pluralize } from '../utils/format';
import { isMember } from '../utils/access';
import { tokens } from '../theme';

const LEVEL_LABEL = { none: 'Нет доступа', view: 'Просмотр', edit: 'Изменение' };
const ROLE_TONE = {
  manager: { bg: '#E8F1FF', fg: '#2F5FD0' },
  accountant: { bg: '#FFF3E0', fg: '#B26A00' },
  financier: { bg: tokens.successSoft, fg: tokens.success },
};

const newPassword = () => {
  const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint32Array(12);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
};

const RoleChip = ({ role, roles }) => {
  const r = roles.find((x) => x.key === role);
  const tone = ROLE_TONE[role] || { bg: tokens.primaryTint, fg: tokens.primaryDark };
  return <Chip size="small" label={r?.label || role} sx={{ bgcolor: tone.bg, color: tone.fg, fontWeight: 700 }} />;
};

// Create / edit an employee: login, role (fills default permissions), section access, accounts.
const MemberDialog = ({ open, member, data, onClose }) => {
  const editing = Boolean(member);
  const [form, setForm] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const role = member?.role || 'manager';
    setForm({
      full_name: member?.full_name || '',
      email: member?.email || '',
      password: editing ? '' : newPassword(),
      role,
      permissions: { ...(member?.permissions || data.roles.find((r) => r.key === role).permissions) },
      allAccounts: !member || member.account_ids == null,
      accounts: member?.account_ids || [],
    });
    setShowPass(!editing);
    setError('');
  }, [open, member, editing, data]);

  if (!open || !form) return null;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const pickRole = (key) => set({ role: key, permissions: { ...data.roles.find((r) => r.key === key).permissions } });
  const roleDefaults = data.roles.find((r) => r.key === form.role).permissions;
  const customized = data.sections.some((s) => (form.permissions[s.key] || 'none') !== roleDefaults[s.key]);
  const toggleAccount = (id) => set({ accounts: form.accounts.includes(id) ? form.accounts.filter((x) => x !== id) : [...form.accounts, id] });
  const valid = (editing || /\S+@\S+\.\S+/.test(form.email)) && (editing ? form.password === '' || form.password.length >= 8 : form.password.length >= 8)
    && (form.allAccounts || form.accounts.length > 0);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const body = {
      full_name: form.full_name, role: form.role, permissions: form.permissions,
      account_ids: form.allAccounts ? null : form.accounts,
      ...(form.password ? { password: form.password } : {}),
      ...(editing ? { active: member.active } : { email: form.email }),
    };
    try {
      if (editing) await teamApi.update(member.id, body);
      else await teamApi.create(body);
      onClose(editing ? { updated: true } : { created: form.email, password: form.password });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose(null)} maxWidth="md" fullWidth scroll="paper"
      PaperProps={{ component: 'form', onSubmit: save }}>
      <DialogTitle>{editing ? `Сотрудник: ${member.full_name || member.email}` : 'Новый сотрудник'}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Вход в MARQIT</Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, mb: 3 }}>
          <TextField label="Имя" value={form.full_name} onChange={(e) => set({ full_name: e.target.value })} size="small" placeholder="Например, Анна — бухгалтер" />
          <TextField label="Email для входа" value={form.email} onChange={(e) => set({ email: e.target.value })} size="small" disabled={editing}
            type="email" required={!editing} helperText={editing ? 'email не меняется' : ''} />
          <TextField label={editing ? 'Новый пароль (необязательно)' : 'Пароль'} value={form.password} onChange={(e) => set({ password: e.target.value })}
            size="small" type={showPass ? 'text' : 'password'} required={!editing}
            helperText={form.password && form.password.length < 8 ? 'не короче 8 символов' : 'передайте его сотруднику'}
            error={Boolean(form.password) && form.password.length < 8}
            InputProps={{ endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Сгенерировать"><IconButton size="small" onClick={() => { set({ password: newPassword() }); setShowPass(true); }} aria-label="Сгенерировать пароль"><AutorenewRoundedIcon fontSize="small" /></IconButton></Tooltip>
                <IconButton size="small" onClick={() => setShowPass((v) => !v)} aria-label={showPass ? 'Скрыть пароль' : 'Показать пароль'}>
                  {showPass ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ) }} />
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Роль</Typography>
        <Box role="radiogroup" aria-label="Роль" sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, mb: 3 }}>
          {data.roles.map((r) => {
            const on = form.role === r.key;
            const tone = ROLE_TONE[r.key];
            return (
              <ButtonBase key={r.key} role="radio" aria-checked={on} onClick={() => pickRole(r.key)}
                sx={{ display: 'block', textAlign: 'left', p: 2, borderRadius: '14px', border: `2px solid ${on ? tokens.primary : tokens.border}`,
                  bgcolor: on ? tokens.primaryTint : tokens.paper, '&:hover': { borderColor: on ? tokens.primary : tokens.borderStrong } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, color: tone.fg }}>{r.label}</Typography>
                  {on && <CheckRoundedIcon sx={{ color: tokens.primary }} fontSize="small" />}
                </Stack>
                <Typography variant="body2" color="text.secondary">{r.description}</Typography>
              </ButtonBase>
            );
          })}
        </Box>

        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Доступ к разделам</Typography>
          {customized && <Button size="small" onClick={() => pickRole(form.role)}>Вернуть как у роли</Button>}
        </Stack>
        <Box sx={{ border: `1px solid ${tokens.border}`, borderRadius: '14px', mb: 3 }}>
          {data.sections.map((s, i) => (
            <Stack key={s.key} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between"
              sx={{ px: 2, py: 1.5, borderTop: i ? `1px solid ${tokens.border}` : 'none' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>{s.label}</Typography>
                <Typography variant="caption" color="text.secondary">{s.description}</Typography>
              </Box>
              <ToggleButtonGroup exclusive size="small" value={form.permissions[s.key] || 'none'}
                onChange={(_, v) => v && set({ permissions: { ...form.permissions, [s.key]: v } })} aria-label={`Доступ: ${s.label}`}
                sx={{ flexShrink: 0, '& .MuiToggleButton-root': { px: 1.5, textTransform: 'none' }, '& .Mui-selected': { bgcolor: `${tokens.primarySoft} !important`, color: `${tokens.primaryDark} !important`, fontWeight: 700 } }}>
                {s.levels.map((l) => <ToggleButton key={l} value={l}>{LEVEL_LABEL[l]}</ToggleButton>)}
              </ToggleButtonGroup>
            </Stack>
          ))}
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Кабинеты</Typography>
        <RadioGroup row value={form.allAccounts ? 'all' : 'some'} onChange={(e) => set({ allAccounts: e.target.value === 'all' })} sx={{ mb: 1 }}>
          <FormControlLabel value="all" control={<Radio size="small" />} label="Все кабинеты, в том числе будущие" />
          <FormControlLabel value="some" control={<Radio size="small" />} label="Только выбранные" />
        </RadioGroup>
        {!form.allAccounts && (
          data.accounts.length === 0 ? <Typography variant="body2" color="text.secondary">Сначала подключите кабинеты площадок.</Typography> : (
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' } }}>
              {data.accounts.map((a) => (
                <FormControlLabel key={a.id} sx={{ m: 0, px: 1.5, py: 0.5, borderRadius: '12px', border: `1px solid ${form.accounts.includes(a.id) ? tokens.primary : tokens.border}` }}
                  control={<Checkbox size="small" checked={form.accounts.includes(a.id)} onChange={() => toggleAccount(a.id)} />}
                  label={<Stack direction="row" spacing={1} alignItems="center"><MarketplaceIcon marketplace={a.marketplace} size={20} /><Typography variant="body2">{a.name}</Typography></Stack>} />
              ))}
            </Box>
          )
        )}
        <Alert severity="info" sx={{ mt: 3 }}>
          Подключение кабинетов, тарифы и управление командой всегда остаются только у вас.
        </Alert>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={() => onClose(null)} disabled={saving}>Отмена</Button>
        <Button type="submit" variant="contained" disabled={!valid || saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          {editing ? 'Сохранить' : 'Добавить сотрудника'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const accessSummary = (m, sections) => sections.filter((s) => (m.permissions[s.key] || 'none') !== 'none')
  .map((s) => ({ key: s.key, text: `${s.label}${m.permissions[s.key] === 'edit' ? ' · изменение' : ''}` }));

// The owner's employees: roles, section access and accounts; logins are created here.
const TeamPage = () => {
  const { profile } = useShell();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null); // { member } | { member: null }
  const [created, setCreated] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data: d } = await teamApi.list();
      setData(d);
      setError('');
    } catch (e) {
      setError(apiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const accountName = useMemo(() => Object.fromEntries((data?.accounts || []).map((a) => [a.id, a.name])), [data]);

  if (isMember(profile)) return <Navigate to="/dashboard" replace />;

  const setActive = async (m, active) => {
    setBusy(m.id);
    try {
      await teamApi.update(m.id, { full_name: m.full_name, role: m.role, permissions: m.permissions, account_ids: m.account_ids, active });
      await load();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy('');
    }
  };

  const add = (
    <Button variant="contained" startIcon={<PersonAddAltRoundedIcon />} onClick={() => setDialog({ member: null })} disabled={!data}>
      Добавить сотрудника
    </Button>
  );

  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
      <PageHeader overline="Команда" title="Сотрудники"
        subtitle="Дайте доступ бухгалтеру, менеджерам и финансисту: у каждого свой вход, роль и только нужные разделы и кабинеты."
        actions={add} />

      {error && <Box sx={{ mb: 2 }}><ErrorState message={error} onRetry={load} compact /></Box>}
      {created && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreated(null)}>
          Сотрудник добавлен. Передайте ему данные для входа: <b>{created.created}</b> · пароль <b>{created.password}</b>. Пароль можно сменить в разделе «Настройки».
        </Alert>
      )}

      {!data && !error && <Skeleton variant="rounded" height={260} sx={{ borderRadius: '16px' }} />}

      {data && (
        <>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, mb: 3 }}>
            {data.roles.map((r) => {
              const tone = ROLE_TONE[r.key];
              const count = data.members.filter((m) => m.role === r.key).length;
              return (
                <Card key={r.key}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Chip label={r.label} sx={{ bgcolor: tone.bg, color: tone.fg, fontWeight: 700 }} />
                      <Typography variant="body2" color="text.secondary">{count} {pluralize(count, 'сотрудник', 'сотрудника', 'сотрудников')}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{r.description}</Typography>
                    <Stack spacing={0.5}>
                      {data.sections.map((s) => {
                        const lvl = r.permissions[s.key] || 'none';
                        return (
                          <Stack key={s.key} direction="row" justifyContent="space-between">
                            <Typography variant="caption" sx={{ color: lvl === 'none' ? tokens.inkMuted : tokens.ink }}>{s.label}</Typography>
                            <Typography variant="caption" sx={{ color: lvl === 'none' ? tokens.inkMuted : tokens.primaryDark, fontWeight: 600 }}>{LEVEL_LABEL[lvl]}</Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {data.members.length === 0 ? (
            <Card>
              <CardContent sx={{ py: 6, textAlign: 'center' }}>
                <Box sx={{ width: 64, height: 64, mx: 'auto', mb: 2, borderRadius: '18px', bgcolor: tokens.primaryTint, color: tokens.primaryDark, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <GroupsOutlinedIcon sx={{ fontSize: 34 }} />
                </Box>
                <Typography variant="h3" sx={{ mb: 1 }}>Пока вы работаете один</Typography>
                <Typography color="text.secondary" sx={{ mb: 2.5 }}>Добавьте сотрудника — он войдёт под своим email и увидит только то, что вы откроете.</Typography>
                {add}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                {data.members.map((m, i) => (
                  <Box key={m.id}>
                    {i > 0 && <Divider />}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ px: 3, py: 2.25, opacity: m.active ? 1 : 0.6 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: { md: '0 0 300px' } }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: ROLE_TONE[m.role]?.bg, color: ROLE_TONE[m.role]?.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                          {(m.full_name || m.email).slice(0, 1).toUpperCase()}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700 }} noWrap>{m.full_name || m.email.split('@')[0]}</Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>{m.email}</Typography>
                        </Box>
                      </Stack>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                          <RoleChip role={m.role} roles={data.roles} />
                          {accessSummary(m, data.sections).map((a) => <Chip key={a.key} size="small" variant="outlined" label={a.text} />)}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {m.account_ids == null ? 'Все кабинеты' : m.account_ids.map((id) => accountName[id] || 'кабинет').join(', ')}
                          {' · '}добавлен {formatDate(m.created_at)}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                        <FormControlLabel sx={{ mr: 0.5 }} label={<Typography variant="body2">{m.active ? 'Доступ открыт' : 'Отключён'}</Typography>}
                          control={<Switch checked={m.active} disabled={busy === m.id} onChange={(e) => setActive(m, e.target.checked)} />} />
                        <Tooltip title="Изменить"><IconButton onClick={() => setDialog({ member: m })} aria-label={`Изменить ${m.email}`}><EditOutlinedIcon /></IconButton></Tooltip>
                        <Tooltip title="Удалить"><IconButton onClick={() => setToDelete(m)} aria-label={`Удалить ${m.email}`}><DeleteOutlineRoundedIcon /></IconButton></Tooltip>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
            Количество пользователей в тарифе — ваше решение: доплата за места считается на странице «Тарифы».
          </Typography>
        </>
      )}

      {data && (
        <MemberDialog open={Boolean(dialog)} member={dialog?.member} data={data}
          onClose={async (res) => { setDialog(null); if (res?.created) setCreated(res); if (res) await load(); }} />
      )}
      <ConfirmDialog open={Boolean(toDelete)} danger title="Удалить сотрудника?" confirmLabel="Удалить"
        text={toDelete ? `${toDelete.full_name || toDelete.email} больше не сможет войти в MARQIT. Данные кабинетов не изменятся.` : ''}
        onConfirm={() => teamApi.remove(toDelete.id)} onClose={async (ok) => { setToDelete(null); if (ok) await load(); }} />
    </Box>
  );
};

export default TeamPage;
